import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  MonitoringVisit,
  CreateMonitoringVisitRequest,
  UpdateMonitoringVisitRequest,
  MonitoringVisitListResponse,
  PlanMonitoringVisitRequest,
  ScheduleMonitoringVisitRequest,
  ConfirmMonitoringVisitRequest,
  FinalizeMonitoringVisitRequest,
  MonitoringVisitFilters,
  OutcomesChecklist,
  VisitType,
} from "../pages/MonitoringTracker/types/monitoringVisit.types";
// API Request/Response types
export interface CreateMonitoringVisitApiRequest {
  study_id: string;
  site_id: string;
  visit_type: string;
  visit_category?: string;
  visit_date: string;
  outcomes_checklist?: {
    protocol_followed?: boolean;
    data_quality?: string;
    findings?: string[];
    corrective_actions?: string[];
    essential_docs_present?: boolean;
    deviations_reviewed?: boolean;
    ec_approval_valid?: boolean;
    drug_accountability_verified?: boolean;
    storage_conditions_ok?: boolean;
    sdv_performed?: boolean;
    query_review_done?: boolean;
  };
  notes?: string;
}
export interface UpdateMonitoringVisitApiRequest {
  visit_type?: string;
  visit_category?: string;
  visit_date?: string;
  planned_date?: string;
  checklist_template_id?: string;
  outcomes_checklist?: {
    protocol_followed?: boolean;
    data_quality?: string;
    findings?: string[];
    corrective_actions?: string[];
    essential_docs_present?: boolean;
    deviations_reviewed?: boolean;
    ec_approval_valid?: boolean;
    drug_accountability_verified?: boolean;
    storage_conditions_ok?: boolean;
    sdv_performed?: boolean;
    query_review_done?: boolean;
  };
  findings_summary?: string;
  notes?: string;
  status?: string;
}
export interface PlanMonitoringVisitApiRequest {
  study_id: string;
  site_id: string;
  visit_type: string;
  visit_category?: string;
  planned_date: string;
  notes?: string;
}
/**
 * @deprecated Use PlanMonitoringVisitApiRequest instead
 */
export interface ScheduleMonitoringVisitApiRequest {
  study_id: string;
  site_id: string;
  visit_type: string;
  visit_category?: string;
  scheduled_date: string;
  notes?: string;
}
export interface ConfirmMonitoringVisitApiRequest {
  visit_date: string;
  checklist_template_id?: string;
  outcomes_checklist?: CreateMonitoringVisitApiRequest["outcomes_checklist"];
  notes?: string;
}
export interface FinalizeMonitoringVisitApiRequest {
  findings_summary: string;
  outcomes_checklist?: CreateMonitoringVisitApiRequest["outcomes_checklist"];
  notes?: string;
}
export interface MonitoringVisitApiResponse {
  id: string;
  tenantId: string;
  studyId: string;
  siteId: string;
  craUserId: string;
  visitType: string;
  visitCategory?: string;
  visitDate: string | null;
  plannedDate?: string | null;
  /** @deprecated Use plannedDate instead */
  scheduledDate?: string | null;
  status?: string;
  outcomesChecklist?: Record<string, unknown>; // Flexible JSON structure from backend
  findingsSummary?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Relations that might be included in API response
  study?: {
    id: string;
    title?: string;
    name?: string;
  };
  site?: {
    id: string;
    name: string;
  };
  craUser?: {
    id: string;
    name: string;
    email?: string;
  };
}
export interface MonitoringVisitListApiResponse {
  items: MonitoringVisitApiResponse[];
  total: number;
  page: number;
  limit: number;
}
export interface IMonitoringVisitRepository {
  createVisit(visit: CreateMonitoringVisitRequest): Promise<MonitoringVisit>;
  getVisits(
    page?: number,
    limit?: number,
    filters?: MonitoringVisitFilters,
  ): Promise<MonitoringVisitListResponse>;
  getVisitById(id: string): Promise<MonitoringVisit>;
  updateVisit(
    id: string,
    updates: UpdateMonitoringVisitRequest,
  ): Promise<MonitoringVisit>;
  deleteVisit(id: string): Promise<void>;
  getVisitsByStudy(studyId: string): Promise<MonitoringVisit[]>;
  getVisitsBySite(siteId: string): Promise<MonitoringVisit[]>;
  planVisit(visit: PlanMonitoringVisitRequest): Promise<MonitoringVisit>;
  /** @deprecated Use planVisit instead */
  scheduleVisit(
    visit: ScheduleMonitoringVisitRequest,
  ): Promise<MonitoringVisit>;
  confirmVisit(
    id: string,
    data: ConfirmMonitoringVisitRequest,
  ): Promise<MonitoringVisit>;
  finalizeVisit(
    id: string,
    data: FinalizeMonitoringVisitRequest,
  ): Promise<MonitoringVisit>;
  uploadVisitAttachment(visitId: string, file: File): Promise<void>;
}
export class MonitoringVisitRepository implements IMonitoringVisitRepository {
  private mapOutcomesChecklistToApi(
    checklist: OutcomesChecklist | undefined,
  ): CreateMonitoringVisitApiRequest["outcomes_checklist"] {
    if (!checklist) {
      return undefined;
    }
    return {
      protocol_followed: checklist.protocol_followed,
      data_quality: checklist.data_quality,
      findings: checklist.findings,
      corrective_actions: checklist.corrective_actions,
      essential_docs_present: checklist.essential_docs_present,
      deviations_reviewed: checklist.deviations_reviewed,
      ec_approval_valid: checklist.ec_approval_valid,
      drug_accountability_verified: checklist.drug_accountability_verified,
      storage_conditions_ok: checklist.storage_conditions_ok,
      sdv_performed: checklist.sdv_performed,
      query_review_done: checklist.query_review_done,
    };
  }
  // Transform frontend request to API format
  private transformCreateRequest(
    request: CreateMonitoringVisitRequest,
  ): CreateMonitoringVisitApiRequest {
    return {
      study_id: request.studyId,
      site_id: request.siteId,
      visit_type: request.visitType,
      visit_category: request.visitCategory?.trim() ?? undefined,
      visit_date: request.visitDate,
      outcomes_checklist: this.mapOutcomesChecklistToApi(
        request.outcomesChecklist,
      ),
      notes: request.notes,
    };
  }
  // Transform API response to frontend format
  private transformApiResponse(
    apiResponse: MonitoringVisitApiResponse,
  ): MonitoringVisit {
    // Map outcomes checklist from backend's flexible JSON structure to frontend format
    // Backend can have any structure, so we try to map common fields
    const checklist = apiResponse.outcomesChecklist as
      OutcomesChecklist | undefined;
    const outcomes = checklist
      ? {
          regulatory: {
            essentialDocsPresent: Boolean(
              checklist.protocol_followed ??
              checklist.essentialDocsPresent ??
              checklist.essential_docs_present,
            ),
            deviationsReviewed: Boolean(
              checklist.deviationsReviewed ?? checklist.deviations_reviewed,
            ),
            ecApprovalValid: Boolean(
              checklist.ecApprovalValid ?? checklist.ec_approval_valid,
            ),
          },
          pharmacy: {
            drugAccountabilityVerified: Boolean(
              checklist.drugAccountabilityVerified ??
              checklist.drug_accountability_verified,
            ),
            storageConditionsOK: Boolean(
              checklist.storageConditionsOK ?? checklist.storage_conditions_ok,
            ),
          },
          sourceData: {
            sdvPerformed: Boolean(
              checklist.sdvPerformed ?? checklist.sdv_performed,
            ),
            queryReviewDone: Boolean(
              checklist.queryReviewDone ?? checklist.query_review_done,
            ),
          },
        }
      : undefined;
    const findingsList = Array.isArray(checklist?.findings)
      ? checklist.findings
      : undefined;
    const findingsSummary =
      apiResponse.findingsSummary ??
      findingsList?.join(", ") ??
      apiResponse.notes?.substring(0, 100) ??
      "No findings recorded";
    // Map status from API, defaulting to Draft if not provided
    let status:
      "Draft" | "Planned" | "In Progress" | "Finalized" | "Cancelled" = "Draft";
    if (apiResponse.status) {
      // Map backend status to frontend status
      const statusMap: Record<
        string,
        "Draft" | "Planned" | "In Progress" | "Finalized" | "Cancelled"
      > = {
        DRAFT: "Draft",
        PLANNED: "Planned",
        // Backward compatibility for old data
        SCHEDULED: "Planned",
        IN_PROGRESS: "In Progress",
        FINALIZED: "Finalized",
        CANCELLED: "Cancelled",
      };
      status = statusMap[apiResponse.status.toUpperCase()] ?? "Draft";
    }
    // Extract study name from relation or leave empty (will be enriched later)
    const studyName = apiResponse.study?.title ?? apiResponse.study?.name ?? "";
    // Extract site name from relation or leave empty (will be enriched later)
    const siteName = apiResponse.site?.name ?? "";
    // Extract CRA name from relation or leave empty (will be enriched later)
    const craName = apiResponse.craUser?.name ?? "";
    const raw = checklist;
    const outcomesChecklist: OutcomesChecklist | undefined = raw
      ? {
          protocol_followed:
            typeof raw.protocol_followed === "boolean"
              ? raw.protocol_followed
              : undefined,
          data_quality:
            typeof raw.data_quality === "string" ? raw.data_quality : undefined,
          findings: Array.isArray(raw.findings) ? raw.findings : undefined,
          corrective_actions: Array.isArray(raw.corrective_actions)
            ? raw.corrective_actions
            : undefined,
          essential_docs_present:
            typeof raw.essential_docs_present === "boolean"
              ? raw.essential_docs_present
              : typeof raw.essentialDocsPresent === "boolean"
                ? raw.essentialDocsPresent
                : undefined,
          deviations_reviewed:
            typeof raw.deviations_reviewed === "boolean"
              ? raw.deviations_reviewed
              : typeof raw.deviationsReviewed === "boolean"
                ? raw.deviationsReviewed
                : undefined,
          ec_approval_valid:
            typeof raw.ec_approval_valid === "boolean"
              ? raw.ec_approval_valid
              : typeof raw.ecApprovalValid === "boolean"
                ? raw.ecApprovalValid
                : undefined,
          drug_accountability_verified:
            typeof raw.drug_accountability_verified === "boolean"
              ? raw.drug_accountability_verified
              : typeof raw.drugAccountabilityVerified === "boolean"
                ? raw.drugAccountabilityVerified
                : undefined,
          storage_conditions_ok:
            typeof raw.storage_conditions_ok === "boolean"
              ? raw.storage_conditions_ok
              : typeof raw.storageConditionsOK === "boolean"
                ? raw.storageConditionsOK
                : undefined,
          sdv_performed:
            typeof raw.sdv_performed === "boolean"
              ? raw.sdv_performed
              : typeof raw.sdvPerformed === "boolean"
                ? raw.sdvPerformed
                : undefined,
          query_review_done:
            typeof raw.query_review_done === "boolean"
              ? raw.query_review_done
              : typeof raw.queryReviewDone === "boolean"
                ? raw.queryReviewDone
                : undefined,
        }
      : undefined;
    return {
      id: apiResponse.id,
      visitDate: apiResponse.visitDate,
      plannedDate: apiResponse.plannedDate ?? apiResponse.scheduledDate ?? null,
      visitType: apiResponse.visitType as VisitType,
      visitCategory: apiResponse.visitCategory ?? "",
      studyId: apiResponse.studyId,
      studyName,
      siteId: apiResponse.siteId,
      siteName,
      findingsSummary,
      completedTasks: 0, // Not in API, default to 0
      pendingTasks: 0, // Not in API, default to 0
      status,
      craId: apiResponse.craUserId,
      craName,
      notes: apiResponse.notes,
      outcomes,
      outcomesChecklist,
      createdAt: apiResponse.createdAt,
      updatedAt: apiResponse.updatedAt,
    };
  }
  async createVisit(
    visit: CreateMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    const apiRequest = this.transformCreateRequest(visit);
    const response: ApiResponse<MonitoringVisitApiResponse> =
      await httpCoreService.post<
        MonitoringVisitApiResponse,
        CreateMonitoringVisitApiRequest
      >("/monitoring-visits", apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to create monitoring visit",
      );
    }
    if (!response.data) {
      throw new Error(
        "Monitoring visit creation succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  async getVisits(
    page = 1,
    limit = 10,
    filters?: MonitoringVisitFilters,
  ): Promise<MonitoringVisitListResponse> {
    const params: Record<string, string | number> = {
      page,
      limit,
    };
    // Add filter parameters if provided
    if (filters) {
      if (filters.studyId && filters.studyId !== "all") {
        params.study_id = filters.studyId;
      }
      if (filters.siteId && filters.siteId !== "all") {
        params.site_id = filters.siteId;
      }
      if (filters.visitType && filters.visitType !== "all") {
        params.visit_type = filters.visitType;
      }
      if (filters.status && filters.status !== "all") {
        params.status = filters.status;
      }
      if (filters.craUserId) {
        params.cra_user_id = filters.craUserId;
      }
      if (filters.fromDate) {
        params.from_date = filters.fromDate;
      }
      if (filters.toDate) {
        params.to_date = filters.toDate;
      }
    }
    const response: ApiResponse<MonitoringVisitListApiResponse> =
      await httpCoreService.get<MonitoringVisitListApiResponse>(
        `/monitoring-visits`,
        {
          params,
        },
      );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch monitoring visits",
      );
    }
    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }
    // Response data contains { items, total, page, limit }
    const listData = response.data;
    // Validate that listData has the expected structure
    if (!listData || typeof listData !== "object") {
      throw new Error("Invalid response data structure from server");
    }
    // Ensure items is an array
    const items = listData.items || [];
    if (!Array.isArray(items)) {
      throw new Error("Response items is not an array");
    }
    return {
      visits: items.map((item) => this.transformApiResponse(item)),
      total: listData.total || 0,
      page: listData.page || page,
      limit: listData.limit || limit,
    };
  }
  async getVisitById(id: string): Promise<MonitoringVisit> {
    const response: ApiResponse<MonitoringVisitApiResponse> =
      await httpCoreService.get<MonitoringVisitApiResponse>(
        `/monitoring-visits/${id}`,
      );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch monitoring visit",
      );
    }
    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  async updateVisit(
    id: string,
    updates: UpdateMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    const apiRequest: UpdateMonitoringVisitApiRequest = {
      visit_type: updates.visitType,
      visit_category: updates.visitCategory?.trim() ?? undefined,
      visit_date: updates.visitDate,
      planned_date: updates.plannedDate,
      checklist_template_id: updates.checklistTemplateId,
      findings_summary: updates.findingsSummary,
      outcomes_checklist: this.mapOutcomesChecklistToApi(
        updates.outcomesChecklist,
      ),
      notes: updates.notes,
    };
    const response: ApiResponse<MonitoringVisitApiResponse> =
      await httpCoreService.put<
        MonitoringVisitApiResponse,
        UpdateMonitoringVisitApiRequest
      >(`/monitoring-visits/${id}`, apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to update monitoring visit",
      );
    }
    if (!response.data) {
      throw new Error(
        "Monitoring visit update succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  async deleteVisit(id: string): Promise<void> {
    const response: ApiResponse<null> = await httpCoreService.delete<null>(
      `/monitoring-visits/${id}`,
    );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to delete monitoring visit",
      );
    }
  }
  async getVisitsByStudy(studyId: string): Promise<MonitoringVisit[]> {
    const response: ApiResponse<MonitoringVisitApiResponse[]> =
      await httpCoreService.get<MonitoringVisitApiResponse[]>(
        `/monitoring-visits/study/${studyId}`,
      );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch study monitoring visits",
      );
    }
    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }
    // Response data is array of visits
    const visitsData = response.data;
    return Array.isArray(visitsData)
      ? visitsData.map((item) => this.transformApiResponse(item))
      : [];
  }
  async getVisitsBySite(siteId: string): Promise<MonitoringVisit[]> {
    const response: ApiResponse<MonitoringVisitApiResponse[]> =
      await httpCoreService.get<MonitoringVisitApiResponse[]>(
        `/monitoring-visits/site/${siteId}`,
      );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch site monitoring visits",
      );
    }
    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }
    // Response data is array of visits
    const visitsData = response.data;
    return Array.isArray(visitsData)
      ? visitsData.map((item) => this.transformApiResponse(item))
      : [];
  }
  async planVisit(visit: PlanMonitoringVisitRequest): Promise<MonitoringVisit> {
    const apiRequest: PlanMonitoringVisitApiRequest = {
      study_id: visit.studyId,
      site_id: visit.siteId,
      visit_type: visit.visitType,
      visit_category: visit.visitCategory?.trim() ?? undefined,
      planned_date: visit.plannedDate,
      notes: visit.notes,
    };
    const response: ApiResponse<MonitoringVisitApiResponse> =
      await httpCoreService.post<
        MonitoringVisitApiResponse,
        PlanMonitoringVisitApiRequest
      >("/monitoring-visits/plan", apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to plan monitoring visit",
      );
    }
    if (!response.data) {
      throw new Error(
        "Monitoring visit planning succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  /**
   * @deprecated Use planVisit instead
   */
  async scheduleVisit(
    visit: ScheduleMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    const apiRequest: ScheduleMonitoringVisitApiRequest = {
      study_id: visit.studyId,
      site_id: visit.siteId,
      visit_type: visit.visitType,
      visit_category: visit.visitCategory?.trim() ?? undefined,
      scheduled_date: visit.scheduledDate,
      notes: visit.notes,
    };
    const response: ApiResponse<MonitoringVisitApiResponse> =
      await httpCoreService.post<
        MonitoringVisitApiResponse,
        ScheduleMonitoringVisitApiRequest
      >("/monitoring-visits/schedule", apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to schedule monitoring visit",
      );
    }
    if (!response.data) {
      throw new Error(
        "Monitoring visit scheduling succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  async confirmVisit(
    id: string,
    data: ConfirmMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    const apiRequest: ConfirmMonitoringVisitApiRequest = {
      visit_date: data.visitDate,
      checklist_template_id: data.checklistTemplateId,
      outcomes_checklist: this.mapOutcomesChecklistToApi(
        data.outcomesChecklist,
      ),
      notes: data.notes,
    };
    const response: ApiResponse<MonitoringVisitApiResponse> =
      await httpCoreService.post<
        MonitoringVisitApiResponse,
        ConfirmMonitoringVisitApiRequest
      >(`/monitoring-visits/${id}/confirm`, apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to confirm monitoring visit",
      );
    }
    if (!response.data) {
      throw new Error(
        "Monitoring visit confirmation succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  async finalizeVisit(
    id: string,
    data: FinalizeMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    const apiRequest: FinalizeMonitoringVisitApiRequest = {
      findings_summary: data.findingsSummary,
      outcomes_checklist: this.mapOutcomesChecklistToApi(
        data.outcomesChecklist,
      ),
      notes: data.notes,
    };
    const response: ApiResponse<MonitoringVisitApiResponse> =
      await httpCoreService.post<
        MonitoringVisitApiResponse,
        FinalizeMonitoringVisitApiRequest
      >(`/monitoring-visits/${id}/finalize`, apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to finalize monitoring visit",
      );
    }
    if (!response.data) {
      throw new Error(
        "Monitoring visit finalization succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  /**
   * Upload a file attachment to a monitoring visit
   */
  async uploadVisitAttachment(visitId: string, file: File): Promise<void> {
    if (!visitId) {
      throw new Error("Visit ID is required for file upload");
    }
    if (!file) {
      throw new Error("File is required");
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await httpCoreService.post(
        `/visit-attachments/upload/${visitId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
      if (!isSuccess) {
        throw new Error(
          (response.error ?? response.message) ||
            `Failed to upload file: ${file.name}`,
        );
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : `Failed to upload file: ${file.name}`;
      throw new Error(message);
    }
  }
}
